import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

export interface INotification {
    id: string;
    title: string;
    description: string;
    type: 'critical' | 'warning' | 'info' | 'success';
    timestamp: string;
    read: boolean;
    category?: string;
    actionRoute?: string;
}

interface NotificationContextType {
    notifications: INotification[];
    unreadCount: number;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    clearNotification: (id: string) => void;
    clearAll: () => void;
    addNotification: (notification: Omit<INotification, 'id' | 'timestamp' | 'read'>) => void;
    updateObservationAlerts: (openCount: number, overdueCount: number) => void;
}

const NotificationContext = createContext<NotificationContextType>({
    notifications: [],
    unreadCount: 0,
    markAsRead: () => {},
    markAllAsRead: () => {},
    clearNotification: () => {},
    clearAll: () => {},
    addNotification: () => {},
    updateObservationAlerts: () => {},
});

export const useNotifications = () => useContext(NotificationContext);

export type Props = {
    children: React.ReactNode;
};

export const NotificationProvider: React.FC<Props> = ({ children }) => {
    const [notifications, setNotifications] = useState<INotification[]>([]);

    const unreadCount = notifications.filter(n => !n.read).length;

    const markAsRead = useCallback((id: string) => {
        setNotifications(prev => 
            prev.map(n => n.id === id ? { ...n, read: true } : n)
        );
    }, []);

    const markAllAsRead = useCallback(() => {
        setNotifications(prev => 
            prev.map(n => ({ ...n, read: true }))
        );
    }, []);

    const clearNotification = useCallback((id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    const clearAll = useCallback(() => {
        setNotifications([]);
    }, []);

    const addNotification = useCallback((newNotif: Omit<INotification, 'id' | 'timestamp' | 'read'>) => {
        const notif: INotification = {
            ...newNotif,
            id: Date.now().toString(),
            timestamp: 'Just now',
            read: false
        };
        setNotifications(prev => [notif, ...prev]);
    }, []);

    const updateObservationAlerts = useCallback((openCount: number, overdueCount: number) => {
        setNotifications(prev => {
            // Find existing observation alerts to check their read state
            const prevOverdue = prev.find(n => n.id === 'obs-overdue');
            const prevOpen = prev.find(n => n.id === 'obs-open');
            
            // Remove previous observation alerts to avoid duplicates
            const cleanPrev = prev.filter(n => n.category !== 'Observation Alert');
            
            const newAlerts: INotification[] = [];
            
            if (overdueCount > 0) {
                let isRead = false;
                if (prevOverdue) {
                    // Extract previous count from description
                    const match = prevOverdue.description.match(/\d+/);
                    const prevCount = match ? parseInt(match[0], 10) : 0;
                    // Only keep as "read" if the new count is less than or equal to the previous count
                    if (overdueCount <= prevCount) {
                        isRead = prevOverdue.read;
                    }
                }
                
                newAlerts.push({
                    id: 'obs-overdue',
                    title: 'Overdue Environmental Observations',
                    description: `You have ${overdueCount} environmental issue observations that are overdue. Action required!`,
                    type: 'critical',
                    timestamp: prevOverdue ? prevOverdue.timestamp : 'Just now',
                    read: isRead,
                    category: 'Observation Alert',
                    actionRoute: 'Environmental Issues Observations'
                });
            }
            
            if (openCount > 0) {
                let isRead = false;
                if (prevOpen) {
                    // Extract previous count from description
                    const match = prevOpen.description.match(/\d+/);
                    const prevCount = match ? parseInt(match[0], 10) : 0;
                    // Only keep as "read" if the new count is less than or equal to the previous count
                    if (openCount <= prevCount) {
                        isRead = prevOpen.read;
                    }
                }
                
                newAlerts.push({
                    id: 'obs-open',
                    title: 'Open Environmental Observations',
                    description: `You have ${openCount} environmental observations currently unresolved.`,
                    type: 'warning',
                    timestamp: prevOpen ? prevOpen.timestamp : 'Just now',
                    read: isRead,
                    category: 'Observation Alert',
                    actionRoute: 'Environmental Issues Observations'
                });
            }
            
            return [...newAlerts, ...cleanPrev];
        });
    }, []);

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                unreadCount,
                markAsRead,
                markAllAsRead,
                clearNotification,
                clearAll,
                addNotification,
                updateObservationAlerts
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};
